import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { userAPI, authAPI, trustAPI, addressAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { StatusBadge, Spinner } from '../components/shared/index';
import { format } from 'date-fns';
import toast from 'react-hot-toast';

function checkPwStrength(pw) {
  return {
    length:    pw.length >= 8,
    uppercase: /[A-Z]/.test(pw),
    lowercase: /[a-z]/.test(pw),
    number:    /\d/.test(pw),
    special:   /[!@#$%^&*(),.?":{}|<>]/.test(pw),
  };
}

export default function ProfilePage() {
  const { user: authUser, updateUser } = useAuth();
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab,     setTab]     = useState('info');

  const [trust, setTrust] = useState(null);

  useEffect(() => {
    fetchProfile();
    trustAPI.getMyTrust().then(({ data }) => setTrust(data.trust)).catch(() => {});
  }, []);

  const fetchProfile = async () => {
    setLoading(true);
    try {
      const { data: res } = await userAPI.getProfile();
      setData(res);
    } catch { toast.error('Failed to load profile.'); }
    finally { setLoading(false); }
  };

  if (loading) return <div className="container section"><Spinner size="lg" centered /></div>;
  if (!data)   return null;

  const { user, stats, activeRentals, orderHistory } = data;

  const trustPoints = trust?.points || 0;
  const trustLevels = [
    { name: 'New', min: 0, max: 99, color: '#9ca3af' },
    { name: 'Trusted', min: 100, max: 299, color: '#0277bd' },
    { name: 'Silver', min: 300, max: 599, color: '#5e35b1' },
    { name: 'Gold', min: 600, max: 999, color: '#b8860b' },
    { name: 'Elite', min: 1000, max: Infinity, color: '#1b5e20' },
  ];
  const trustLevel = trustLevels.find(l => trustPoints >= l.min && trustPoints <= l.max) || trustLevels[0];
  const nextTrustLevel = trustLevels[trustLevels.findIndex(l => trustPoints >= l.min && trustPoints <= l.max) + 1];

  const TABS = [
    { id: 'info',     label: 'Profile' },
    { id: 'security', label: 'Password' },
    { id: 'rentals',  label: 'Active Rentals' + (stats.activeRentals > 0 ? ` (${stats.activeRentals})` : '') },
    { id: 'history',  label: 'Order History' },
  ];

  return (
    <div style={{ background: 'var(--cream)', minHeight: '100vh' }}>

      {/* ══ EDITORIAL HEADER ══════════════════════════════════════════════════ */}
      <div style={{ position: 'relative', overflow: 'hidden', background: 'var(--cream-dark)', borderBottom: '1px solid var(--border)' }}>

        {/* Big watermark initial */}
        <div style={{
          position: 'absolute', right: '-0.05em', top: '-0.15em',
          fontFamily: "'Cormorant Garamond',serif",
          fontSize: 'clamp(160px,22vw,280px)',
          fontWeight: 700, color: 'rgba(184,146,74,0.08)',
          lineHeight: 1, userSelect: 'none', pointerEvents: 'none',
          letterSpacing: '-0.05em',
        }}>
          {user.name.charAt(0).toUpperCase()}
        </div>

        {/* Diagonal gold line accent */}
        <div style={{
          position: 'absolute', top: 0, left: 0, right: 0, height: 3,
          background: 'linear-gradient(90deg, transparent 0%, var(--gold) 30%, var(--gold-light) 70%, transparent 100%)',
        }} />

        <div style={{ maxWidth: 960, margin: '0 auto', padding: 'clamp(36px,5vw,56px) clamp(16px,4vw,40px) 0', position: 'relative', zIndex: 1 }}>

          {/* Top label */}
          <div style={{ fontSize: 9, letterSpacing: '0.35em', color: 'var(--gold)', textTransform: 'uppercase', marginBottom: 16, fontWeight: 700 }}>
            Warbadobe Rentals · Member Profile
          </div>

          {/* Name block */}
          <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 24, flexWrap: 'wrap', marginBottom: 40 }}>
            <div>
              <h1 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 'clamp(40px,7vw,80px)', fontWeight: 300, fontStyle: 'italic', color: 'var(--ink)', lineHeight: 0.95, margin: 0 }}>
                {user.name}
              </h1>
              <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 10, letterSpacing: '0.04em' }}>
                {user.email}
              </div>
            </div>

            {/* Avatar — editorial circle */}
            <div style={{
              width: 'clamp(52px,8vw,72px)', height: 'clamp(52px,8vw,72px)',
              borderRadius: '50%', border: '2px solid var(--gold)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontFamily: "'Cormorant Garamond',serif",
              fontSize: 'clamp(20px,4vw,30px)', color: 'var(--gold)', fontWeight: 600,
              flexShrink: 0, background: 'var(--cream)',
            }}>
              {user.name.charAt(0).toUpperCase()}
            </div>
          </div>

          {/* Stats */}
          <div className="profile-stats-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', borderTop: '1px solid var(--border)' }}>
            {[
              { label: 'Orders',         value: stats.totalOrders },
              { label: 'Active Rentals', value: stats.activeRentals },
              { label: 'Deposit Held',   value: `₹${(stats.depositsHeld || 0).toLocaleString('en-IN')}` },
              { label: 'College',        value: user.isCollegeVerified ? (user.college || 'Verified') : 'Not verified', color: user.isCollegeVerified ? '#2d6a2d' : 'var(--muted)' },
            ].map((st, i) => (
              <div key={i} style={{
                padding: 'clamp(14px,2.2vw,20px) 0',
                paddingRight: i < 3 ? 'clamp(12px,2vw,24px)' : 0,
                paddingLeft: i > 0 ? 'clamp(12px,2vw,24px)' : 0,
                borderRight: i < 3 ? '1px solid var(--border)' : 'none',
              }}>
                <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 'clamp(20px,2.8vw,28px)', color: st.color || 'var(--ink)', fontWeight: 400, lineHeight: 1, marginBottom: 5 }}>
                  {st.value}
                </div>
                <div style={{ fontSize: 9, color: 'var(--muted)', letterSpacing: '0.18em', textTransform: 'uppercase' }}>
                  {st.label}
                </div>
              </div>
            ))}
          </div>

          {/* Warba Score strip */}
          {trust && (
            <div onClick={() => { window.location.href = '/trust'; }}
              style={{ borderTop: '1px solid var(--border)', padding: 'clamp(12px,2vw,16px) clamp(16px,3vw,24px)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', background: 'var(--cream)', transition: 'background 0.15s' }}
              onMouseEnter={e => e.currentTarget.style.background = 'var(--cream-dark)'}
              onMouseLeave={e => e.currentTarget.style.background = 'var(--cream)'}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
                <div>
                  <div style={{ fontSize: 8, fontWeight: 700, letterSpacing: '0.22em', textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 3 }}>Warba Score</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 26, color: 'var(--ink)', fontWeight: 300 }}>{trustPoints}</span>
                    <span style={{ display: 'inline-block', padding: '3px 10px', fontSize: 9, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: trustLevel.color, background: trustLevel.color + '18', border: `1px solid ${trustLevel.color}33` }}>{trustLevel.name}</span>
                    {trust.isBanned && <span style={{ fontSize: 9, fontWeight: 700, color: '#b71c1c', background: '#ffebee', padding: '2px 8px', letterSpacing: '0.1em', textTransform: 'uppercase' }}>Suspended</span>}
                  </div>
                </div>
                {nextTrustLevel && (
                  <div style={{ minWidth: 120 }}>
                    <div style={{ fontSize: 9, color: 'var(--muted)', marginBottom: 4 }}>{nextTrustLevel.min - trustPoints} pts to {nextTrustLevel.name}</div>
                    <div style={{ height: 3, background: 'var(--border)', borderRadius: 2, width: 120 }}>
                      <div style={{ height: '100%', background: 'var(--gold)', borderRadius: 2, width: `${Math.min(100, ((trustPoints - trustLevel.min) / (nextTrustLevel.min - trustLevel.min)) * 100)}%` }} />
                    </div>
                  </div>
                )}
              </div>
              <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--gold)', flexShrink: 0 }}>View Score →</div>
            </div>
          )}

          {/* Tab bar */}
          <div style={{ display: 'flex', overflowX: 'auto', scrollbarWidth: 'none', gap: 0, marginTop: 0 }}>
            {TABS.map(t => (
              <button key={t.id} onClick={() => setTab(t.id)} style={{
                padding: 'clamp(10px,1.8vw,14px) clamp(12px,2vw,20px)',
                fontSize: 10, fontWeight: 700, letterSpacing: '0.14em',
                textTransform: 'uppercase', whiteSpace: 'nowrap',
                background: 'none', border: 'none', cursor: 'pointer',
                color: tab === t.id ? 'var(--gold)' : 'var(--muted)',
                borderBottom: `2px solid ${tab === t.id ? 'var(--gold)' : 'transparent'}`,
                transition: 'color 0.15s, border-color 0.15s',
              }}>
                {t.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ══ TAB CONTENT ══════════════════════════════════════════════════════ */}
      <div style={{ maxWidth: 960, margin: '0 auto', padding: 'clamp(24px,4vw,40px) clamp(16px,4vw,40px)' }}>
        {tab === 'info'     && <ProfileInfoTab user={user} onUpdated={fetchProfile} updateUser={updateUser} trustPoints={trustPoints} trustLevel={trustLevel} nextTrustLevel={nextTrustLevel} trustLimits={trustLevels} />}
        {tab === 'security' && <PasswordTab />}
        {tab === 'rentals'  && <OrderList orders={activeRentals} emptyMsg="No active rentals right now." />}
        {tab === 'history'  && <OrderList orders={orderHistory}  emptyMsg="No completed orders yet." />}
      </div>
    </div>
  );
}

// ── Profile Info Tab ──────────────────────────────────────────────────────────
function ProfileInfoTab({ user, onUpdated, updateUser, trustPoints = 0, trustLevel = { name: 'New', color: '#9ca3af', min: 0 }, nextTrustLevel = null, trustLimits = [] }) {
  const [form,    setForm]    = useState({ name: user.name || '', phone: user.phone || '', college: user.college || '' });
  const [saving,  setSaving]  = useState(false);
  const [editing, setEditing] = useState(false);


  const handleSave = async () => {
    if (form.phone && !/^[6-9]\d{9}$/.test(form.phone)) {
      toast.error('Enter a valid 10-digit Indian mobile number.'); return;
    }
    setSaving(true);
    try {
      const { data } = await userAPI.updateProfile(form);
      updateUser(data.user);
      toast.success('Profile updated!');
      setEditing(false);
      onUpdated();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Update failed.');
    } finally { setSaving(false); }
  };



  const allVerified = user.isEmailVerified && user.isCollegeVerified;

  return (
    <div className="profile-page-layout" style={{ display: 'grid', gridTemplateColumns: '1fr clamp(200px,26%,260px)', gap: 20, alignItems: 'start' }}>

      {/* ── LEFT: info card ── */}
      <div>
        <div style={C.card}>

          {/* Card header */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 24, paddingBottom: 16, borderBottom: '1px solid var(--cream-dark)' }}>
            <div style={C.head}>Personal Information</div>
            {!editing
              ? <button onClick={() => setEditing(true)} style={C.ghostBtn}>Edit</button>
              : <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={() => { setEditing(false); setForm({ name: user.name, phone: user.phone || '', college: user.college || '' }); }} style={C.ghostBtn}>Cancel</button>
                  <button onClick={handleSave} disabled={saving} style={C.solidBtn}>{saving ? 'Saving…' : 'Save'}</button>
                </div>
            }
          </div>

          <Row label="Email" last={false}>
            <span style={C.val}>{user.email}</span>
            <Pill ok={user.isEmailVerified} yes="Verified" no="Unverified" />
            <Chip>Fixed</Chip>
          </Row>

          <Row label="Name" last={false}>
            {editing
              ? <input className="input" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} style={{ maxWidth: 280 }} />
              : <span style={C.val}>{form.name || '—'}</span>}
          </Row>

          <Row label="Phone" last={false}>
            {editing
              ? <input className="input" inputMode="tel" value={form.phone} placeholder="10-digit number"
                  onChange={e => setForm(f => ({ ...f, phone: e.target.value.replace(/\D/g,'').slice(0,10) }))} style={{ maxWidth: 200 }} />
              : <><span style={C.val}>{form.phone || '—'}</span><Pill ok={user.isPhoneVerified} yes="Verified" no="Unverified" /></>}
          </Row>

          <Row label="College" last={false}>
            {editing
              ? <input className="input" value={form.college} onChange={e => setForm(f => ({ ...f, college: e.target.value }))} style={{ maxWidth: 280 }} placeholder="Optional" />
              : <span style={C.val}>{form.college || '—'}</span>}
          </Row>

          <Row label="Role" last={false}>
            <span style={{ ...C.val, textTransform: 'capitalize' }}>{user.role}</span>
          </Row>

          <Row label="Member Since" last>
            <span style={C.val}>{user.createdAt ? format(new Date(user.createdAt), 'MMMM yyyy') : '—'}</span>
          </Row>
        </div>

        <AddressBook user={user} onUpdated={onUpdated} />

        {/* Verification action cards */}
        {!user.isEmailVerified && <EmailVerifyCard email={user.email} />}
        {!user.isPhoneVerified && <PhoneVerifyCard phone={user.phone} onVerified={onUpdated} updateUser={updateUser} />}
      </div>

      {/* ── RIGHT: sidebar ── */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>

        {/* Warba Score block */}
        <div style={{ ...C.card, overflow: 'hidden', borderTop: '3px solid var(--gold)', cursor: 'pointer' }}
          onClick={() => window.location.href = '/trust'}>
          <div style={{ fontSize: 9, letterSpacing: '0.22em', textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 8 }}>Warba Score</div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 8 }}>
            <span style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 36, fontWeight: 300, color: 'var(--ink)', lineHeight: 1 }}>
              {trustPoints}
            </span>
            <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: trustLevel.color, background: trustLevel.color + '18', padding: '2px 8px', border: `1px solid ${trustLevel.color}33` }}>
              {trustLevel.name}
            </span>
          </div>
          {nextTrustLevel && (
            <>
              <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 6 }}>
                {nextTrustLevel.min - trustPoints} pts to unlock ₹{nextTrustLevel.min >= 1000 ? 'unlimited' : (trustLimits.find(l => l.name === nextTrustLevel.name)?.limit || '').toLocaleString('en-IN')} orders
              </div>
              <div style={{ height: 3, background: 'var(--cream-dark)', borderRadius: 2 }}>
                <div style={{ height: '100%', background: 'var(--gold)', borderRadius: 2, width: `${Math.min(100,((trustPoints - trustLevel.min)/(nextTrustLevel.min - trustLevel.min))*100)}%` }} />
              </div>
            </>
          )}
          <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--gold)', marginTop: 12 }}>View Score →</div>
        </div>

        {/* All verified banner */}
        {allVerified && (
          <div style={{ ...C.card, background: '#f0f7f0', borderColor: 'rgba(45,106,45,0.3)', borderTop: '3px solid #4caf50' }}>
            <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 20, fontStyle: 'italic', color: '#2d6a2d', marginBottom: 6 }}>Fully Verified</div>
            <div style={{ fontSize: 12, color: '#388e3c', lineHeight: 1.7 }}>Email and college verified. Unlimited orders unlocked.</div>
          </div>
        )}

        {/* Quick links */}
        <div style={C.card}>
          <div style={{ fontSize: 9, letterSpacing: '0.22em', textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 12 }}>Quick Links</div>
          {[
            { to: '/browse',   label: 'Browse Collection' },
            { to: '/orders',   label: 'My Orders' },
            { to: '/wishlist', label: 'Wishlist' },
            { to: '/cart',     label: 'Cart' },
          ].map(l => (
            <Link key={l.to} to={l.to} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '9px 0', borderBottom: '1px solid var(--cream-dark)', fontSize: 12, color: 'var(--ink)', textDecoration: 'none', transition: 'color 0.15s' }}
              onMouseEnter={e => e.currentTarget.style.color='var(--gold)'}
              onMouseLeave={e => e.currentTarget.style.color='var(--ink)'}>
              {l.label}
              <span style={{ color: 'var(--border-strong)', fontSize: 14 }}>›</span>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Email Verify Card ─────────────────────────────────────────────────────────
// ── Address Book ──────────────────────────────────────────────────────────────
const BLANK_ADDR = { label: 'Home', name: '', phone: '', street: '', city: '', state: '', pincode: '' };

function AddressBook({ user, onUpdated }) {
  const [addresses,   setAddresses]   = useState(user.savedAddresses || []);
  const [showForm,    setShowForm]    = useState(false);

  // Sync local state whenever parent re-fetches user (fixes display-after-save bug)
  useEffect(() => {
    setAddresses(user.savedAddresses || []);
  }, [JSON.stringify(user.savedAddresses)]); // eslint-disable-line
  const [editId,      setEditId]      = useState(null); // null = new
  const [form,        setForm]        = useState(BLANK_ADDR);
  const [saving,      setSaving]      = useState(false);
  const [deletingId,  setDeletingId]  = useState(null);

  const openAdd = () => { setForm(BLANK_ADDR); setEditId(null); setShowForm(true); };
  const openEdit = (addr) => {
    setForm({ label: addr.label || 'Home', name: addr.name || '', phone: addr.phone || '', street: addr.street || '', city: addr.city || '', state: addr.state || '', pincode: addr.pincode || '' });
    setEditId(addr._id);
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.street || !form.city || !form.pincode) { toast.error('Street, city and pincode are required.'); return; }
    if (!/^\d{6}$/.test(form.pincode)) { toast.error('Enter a valid 6-digit pincode.'); return; }
    if (form.phone && !/^[6-9]\d{9}$/.test(form.phone)) { toast.error('Enter a valid 10-digit mobile number.'); return; }
    setSaving(true);
    try {
      let res;
      if (editId) {
        res = await addressAPI.update(editId, form);
      } else {
        res = await addressAPI.add({ ...form, isDefault: addresses.length === 0 });
      }
      setAddresses(res.data.addresses);
      setShowForm(false);
      toast.success(editId ? 'Address updated!' : 'Address added!');
      onUpdated();
    } catch (e) { toast.error(e.response?.data?.message || 'Could not save.'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this address?')) return;
    setDeletingId(id);
    try {
      const res = await addressAPI.remove(id);
      setAddresses(res.data.addresses);
      onUpdated();
    } catch (e) { toast.error(e.response?.data?.message || 'Could not delete.'); }
    finally { setDeletingId(null); }
  };

  const handleSetDefault = async (id) => {
    try {
      const res = await addressAPI.setDefault(id);
      setAddresses(res.data.addresses);
      onUpdated();
    } catch (e) { toast.error('Could not update default.'); }
  };

  const LABELS = ['Home', 'Work', 'Other'];

  return (
    <div style={C.card}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 20, paddingBottom: 14, borderBottom: '1px solid var(--cream-dark)' }}>
        <div style={C.head}>Delivery Addresses</div>
        {!showForm && addresses.length < 5 && (
          <button onClick={openAdd} style={C.ghostBtn}>+ Add Address</button>
        )}
      </div>

      {/* Address list */}
      {!showForm && (
        <>
          {addresses.length === 0 && (
            <div style={{ fontSize: 13, color: 'var(--muted)', fontStyle: 'italic', marginBottom: 12 }}>
              No addresses saved yet. Add one to speed up checkout.
            </div>
          )}
          {addresses.map(addr => (
            <div key={addr._id} style={{ border: '1px solid var(--border)', padding: '12px 14px', marginBottom: 10, position: 'relative', background: addr.isDefault ? 'rgba(184,146,74,0.04)' : 'white', borderColor: addr.isDefault ? 'rgba(184,146,74,0.4)' : 'var(--border)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                    <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--ink)' }}>{addr.label || 'Address'}</span>
                    {addr.isDefault && <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--gold)', background: 'rgba(184,146,74,0.12)', padding: '2px 7px', border: '1px solid rgba(184,146,74,0.3)' }}>Default</span>}
                  </div>
                  {addr.name && <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink)' }}>{addr.name}</div>}
                  <div style={{ fontSize: 13, color: 'var(--ink)', lineHeight: 1.7 }}>{addr.street}</div>
                  <div style={{ fontSize: 12, color: 'var(--muted)' }}>{addr.city}{addr.state ? `, ${addr.state}` : ''} — {addr.pincode}</div>
                  {addr.phone && <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 2 }}>📞 {addr.phone}</div>}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, flexShrink: 0 }}>
                  <button onClick={() => openEdit(addr)} style={{ ...C.ghostBtn, fontSize: 11, padding: '4px 10px' }}>Edit</button>
                  {!addr.isDefault && (
                    <button onClick={() => handleSetDefault(addr._id)} style={{ ...C.ghostBtn, fontSize: 11, padding: '4px 10px' }}>Set Default</button>
                  )}
                  <button onClick={() => handleDelete(addr._id)} disabled={deletingId === addr._id}
                    style={{ fontSize: 11, padding: '4px 10px', border: '1px solid #f44336', background: 'none', color: '#f44336', cursor: 'pointer' }}>
                    {deletingId === addr._id ? '...' : 'Delete'}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </>
      )}

      {/* Add / Edit form */}
      {showForm && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {/* Label selector */}
          <div className="form-group">
            <label className="form-label">Label</label>
            <div style={{ display: 'flex', gap: 8 }}>
              {LABELS.map(l => (
                <button key={l} onClick={() => setForm(f => ({ ...f, label: l }))}
                  style={{ padding: '6px 14px', fontSize: 12, border: '1px solid', cursor: 'pointer', transition: 'all 0.15s',
                    borderColor: form.label === l ? 'var(--gold)' : 'var(--border)',
                    background: form.label === l ? 'rgba(184,146,74,0.08)' : 'white',
                    color: form.label === l ? 'var(--gold)' : 'var(--muted)', fontWeight: form.label === l ? 700 : 400 }}>
                  {l}
                </button>
              ))}
            </div>
          </div>

          {/* Name + Phone */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div className="form-group">
              <label className="form-label">Recipient Name</label>
              <input className="input" value={form.name} placeholder="Full name"
                onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
            </div>
            <div className="form-group">
              <label className="form-label">Phone Number</label>
              <input className="input" inputMode="tel" value={form.phone} placeholder="10-digit number"
                onChange={e => setForm(f => ({ ...f, phone: e.target.value.replace(/\D/g,'').slice(0,10) }))} />
            </div>
          </div>

          {/* Street */}
          <div className="form-group">
            <label className="form-label">House / Flat / Street *</label>
            <input className="input" value={form.street} placeholder="e.g. 42B, MG Road, Sarabha Nagar"
              onChange={e => setForm(f => ({ ...f, street: e.target.value }))} />
          </div>

          {/* City + Pincode + State */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
            <div className="form-group">
              <label className="form-label">City *</label>
              <input className="input" value={form.city} placeholder="Ludhiana"
                onChange={e => setForm(f => ({ ...f, city: e.target.value }))} />
            </div>
            <div className="form-group">
              <label className="form-label">Pincode *</label>
              <input className="input" inputMode="numeric" maxLength={6} value={form.pincode} placeholder="141001"
                onChange={e => setForm(f => ({ ...f, pincode: e.target.value.replace(/\D/g,'').slice(0,6) }))} />
            </div>
            <div className="form-group">
              <label className="form-label">State</label>
              <input className="input" value={form.state} placeholder="Punjab"
                onChange={e => setForm(f => ({ ...f, state: e.target.value }))} />
            </div>
          </div>

          <div style={{ fontSize: 11, color: 'var(--muted)' }}>
            This address can be selected at checkout. Up to 5 addresses allowed.
          </div>

          <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
            <button onClick={() => setShowForm(false)} style={C.ghostBtn}>Cancel</button>
            <button onClick={handleSave} disabled={saving} style={{ ...C.solidBtn, flex: 1 }}>
              {saving ? 'Saving…' : (editId ? 'Update Address' : 'Save Address')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}


function EmailVerifyCard({ email }) {
  const [cooldown, setCooldown] = useState(0);
  const [sending,  setSending]  = useState(false);
  const [sent,     setSent]     = useState(false);

  const handleResend = async () => {
    if (cooldown > 0 || sending) return;
    setSending(true);
    try {
      await authAPI.resendVerification({ email });
      toast.success('Verification email sent!');
      setSent(true);
      let secs = 60; setCooldown(secs);
      const t = setInterval(() => { secs--; setCooldown(secs); if (secs <= 0) clearInterval(t); }, 1000);
    } catch (e) { toast.error(e.response?.data?.message || 'Failed to resend.'); }
    finally { setSending(false); }
  };

  return (
    <div style={{ ...C.card, marginTop: 12, borderTop: '3px solid #e65100' }}>
      <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 18, color: '#e65100', marginBottom: 6 }}>Email Unverified</div>
      <div style={{ fontSize: 12, color: 'var(--muted)', lineHeight: 1.7, marginBottom: 12 }}>
        {sent ? `We sent a link to ${email}. Click it, then refresh.` : `Verify ${email} to enable ordering.`}
      </div>
      <button onClick={handleResend} disabled={cooldown > 0 || sending}
        style={{ ...C.solidBtn, opacity: cooldown > 0 ? 0.55 : 1, cursor: cooldown > 0 ? 'not-allowed' : 'pointer' }}>
        {sending ? 'Sending…' : cooldown > 0 ? `Resend in ${cooldown}s` : sent ? 'Resend Email' : 'Send Verification Email'}
      </button>
    </div>
  );
}

// ── Phone Verify Card ─────────────────────────────────────────────────────────
function PhoneVerifyCard({ phone, onVerified }) {
  const [step,     setStep]     = useState('idle');
  const [otp,      setOtp]      = useState('');
  const [sending,  setSending]  = useState(false);
  const [checking, setChecking] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [error,    setError]    = useState('');

  const startCooldown = () => {
    let secs = 60; setCooldown(secs);
    const t = setInterval(() => { secs--; setCooldown(secs); if (secs <= 0) clearInterval(t); }, 1000);
  };

  const handleSendOtp = async () => {
    if (!phone) { toast.error('Add a phone number first.'); return; }
    if (cooldown > 0 || sending) return;
    setSending(true); setError('');
    try {
      const { data } = await authAPI.sendPhoneOtp();
      toast.success(data.message || 'OTP sent!');
      setStep('sent'); startCooldown();
    } catch (e) {
      const msg = e.response?.data?.message || 'Failed to send OTP.';
      const secsMatch = msg.match(/(\d+) second/);
      if (secsMatch) { const s = parseInt(secsMatch[1]); setCooldown(s); const t = setInterval(() => { setCooldown(p => { if (p <= 1) { clearInterval(t); return 0; } return p - 1; }); }, 1000); }
      setError(msg); toast.error(msg);
    } finally { setSending(false); }
  };

  const handleVerify = async () => {
    if (otp.length !== 6) { setError('Enter the 6-digit code.'); return; }
    setChecking(true); setError('');
    try {
      await authAPI.verifyPhone({ otp });
      toast.success('Phone verified!');
      setStep('done'); onVerified();
    } catch (e) {
      const msg = e.response?.data?.message || 'Invalid OTP.';
      setError(msg);
      if (msg.toLowerCase().includes('too many')) { setStep('idle'); setOtp(''); setCooldown(0); }
    } finally { setChecking(false); }
  };

  if (step === 'done') return null;

  return (
    <div style={{ ...C.card, marginTop: 12, borderTop: '3px solid var(--border-strong)' }}>
      <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 18, color: 'var(--ink)', marginBottom: 6 }}>Verify Phone</div>
      <div style={{ fontSize: 12, color: 'var(--muted)', lineHeight: 1.7, marginBottom: 10 }}>
        {phone ? <>Ending in <strong>**{phone.slice(-2)}</strong> — optional but recommended.</> : 'Add a phone number first.'}
      </div>
      <div style={{ background: 'var(--gold-pale)', border: '1px solid var(--border)', padding: '8px 12px', fontSize: 11, color: 'var(--muted)', marginBottom: 12, lineHeight: 1.6 }}>
        SMS verification is temporarily unavailable. You can still place orders.
      </div>
      {phone && step === 'idle' && (
        <button onClick={handleSendOtp} disabled={sending || cooldown > 0} style={{ ...C.ghostBtn, opacity: cooldown > 0 ? 0.55 : 1 }}>
          {sending ? 'Sending…' : cooldown > 0 ? `Resend in ${cooldown}s` : 'Send OTP'}
        </button>
      )}
      {phone && step === 'sent' && (
        <div>
          <div style={{ fontSize: 12, color: '#2d6a2d', marginBottom: 10, background: '#f0f7f0', padding: '8px 12px', border: '1px solid rgba(45,106,45,0.2)' }}>
            OTP sent — enter the 6-digit code below.
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
            <input className="input" type="tel" inputMode="numeric" value={otp}
              onChange={e => { setOtp(e.target.value.replace(/\D/g,'').slice(0,6)); setError(''); }}
              placeholder="000000" maxLength={6} autoFocus
              style={{ maxWidth: 140, letterSpacing: '0.3em', fontSize: 16, fontWeight: 700, textAlign: 'center' }} />
            <button onClick={handleVerify} disabled={checking || otp.length !== 6} style={C.solidBtn}>
              {checking ? 'Checking…' : 'Verify'}
            </button>
          </div>
          <div style={{ display: 'flex', gap: 14 }}>
            <button onClick={handleSendOtp} disabled={cooldown > 0 || sending} style={{ fontSize: 11, background: 'none', border: 'none', cursor: 'pointer', color: 'var(--muted)', textDecoration: 'underline' }}>
              {cooldown > 0 ? `Resend in ${cooldown}s` : 'Resend'}
            </button>
            <button onClick={() => { setStep('idle'); setOtp(''); setError(''); }} style={{ fontSize: 11, background: 'none', border: 'none', cursor: 'pointer', color: 'var(--muted)', textDecoration: 'underline' }}>Cancel</button>
          </div>
        </div>
      )}
      {error && <div style={{ fontSize: 12, color: '#8b2020', marginTop: 8 }}>{error}</div>}
    </div>
  );
}

// ── Password Tab ──────────────────────────────────────────────────────────────
function PasswordTab() {
  const [form,   setForm]   = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [show,   setShow]   = useState({ current: false, new: false, confirm: false });
  const [saving, setSaving] = useState(false);

  const checks   = checkPwStrength(form.newPassword);
  const pwStrong = Object.values(checks).every(Boolean);
  const bars     = Object.values(checks).filter(Boolean).length;
  const barColor = ['#c0392b','#e67e22','#f1c40f','#7dba4c','#2d6a2d'][bars - 1] || '#ede8dc';

  const handleChange = async (e) => {
    e.preventDefault();
    if (!pwStrong)                                 { toast.error('Password is too weak.');      return; }
    if (form.newPassword !== form.confirmPassword) { toast.error('Passwords do not match.');    return; }
    setSaving(true);
    try {
      await userAPI.changePassword(form);
      toast.success('Password changed!');
      setForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to change password.');
    } finally { setSaving(false); }
  };

  return (
    <div style={{ maxWidth: 440 }}>
      <div style={C.card}>
        <div style={{ ...C.head, marginBottom: 24, paddingBottom: 16, borderBottom: '1px solid var(--cream-dark)' }}>Change Password</div>
        <form onSubmit={handleChange}>
          {[
            { key: 'currentPassword', label: 'Current Password', showKey: 'current' },
            { key: 'newPassword',     label: 'New Password',     showKey: 'new'     },
            { key: 'confirmPassword', label: 'Confirm Password', showKey: 'confirm' },
          ].map(f => (
            <div key={f.key} style={{ marginBottom: 20 }}>
              <label style={{ display: 'block', fontSize: 9, fontWeight: 700, letterSpacing: '0.2em', textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 7 }}>
                {f.label}
              </label>
              <div style={{ position: 'relative' }}>
                <input className="input" type={show[f.showKey] ? 'text' : 'password'}
                  value={form[f.key]} onChange={e => setForm(p => ({ ...p, [f.key]: e.target.value }))}
                  placeholder="••••••••" required style={{ paddingRight: 56, width: '100%' }}
                  autoComplete={f.key === 'currentPassword' ? 'current-password' : 'new-password'} />
                <button type="button" tabIndex={-1}
                  onClick={() => setShow(s => ({ ...s, [f.showKey]: !s[f.showKey] }))}
                  style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', fontSize: 9, fontWeight: 700, color: 'var(--muted)', letterSpacing: '0.1em' }}>
                  {show[f.showKey] ? 'HIDE' : 'SHOW'}
                </button>
              </div>
              {f.key === 'newPassword' && form.newPassword && (
                <div style={{ marginTop: 10 }}>
                  <div style={{ display: 'flex', gap: 3, marginBottom: 7 }}>
                    {[1,2,3,4,5].map(i => (
                      <div key={i} style={{ flex: 1, height: 2, background: i <= bars ? barColor : 'var(--cream-dark)', transition: 'background 0.25s' }} />
                    ))}
                  </div>
                  <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                    {[[checks.length,'8+ chars'],[checks.uppercase,'A–Z'],[checks.lowercase,'a–z'],[checks.number,'0–9'],[checks.special,'Symbol']].map(([ok,lbl]) => (
                      <span key={lbl} style={{ fontSize: 9, letterSpacing: '0.08em', color: ok ? '#2d6a2d' : 'var(--muted)', fontWeight: 700 }}>
                        {ok ? '✓' : '○'} {lbl}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
          <button type="submit" disabled={saving || !pwStrong} style={{
            width: '100%', padding: '11px 0', fontSize: 10, fontWeight: 700,
            letterSpacing: '0.14em', textTransform: 'uppercase',
            background: pwStrong ? 'var(--ink)' : 'var(--cream-dark)',
            color: pwStrong ? 'var(--cream)' : 'var(--muted)',
            border: 'none', cursor: pwStrong ? 'pointer' : 'not-allowed',
            transition: 'all 0.2s', marginTop: 4,
          }}>
            {saving ? 'Updating…' : 'Update Password'}
          </button>
        </form>
      </div>
    </div>
  );
}

// ── Order List ────────────────────────────────────────────────────────────────
function OrderList({ orders, emptyMsg }) {
  if (!orders.length) return (
    <div style={{ textAlign: 'center', padding: '80px 0' }}>
      <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 'clamp(32px,5vw,48px)', fontStyle: 'italic', fontWeight: 300, color: 'var(--cream-dark)', marginBottom: 8, lineHeight: 1 }}>
        Nothing here yet
      </div>
      <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 24 }}>{emptyMsg}</div>
      <Link to="/browse" style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.16em', textTransform: 'uppercase', color: 'var(--gold)', textDecoration: 'none', borderBottom: '1px solid var(--gold)', paddingBottom: 2 }}>
        Browse Collection
      </Link>
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {orders.map(order => (
        <div key={order._id} style={C.card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap' }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 6 }}>
                <span style={{ fontFamily: 'monospace', fontSize: 10, background: 'var(--cream-dark)', padding: '2px 8px', border: '1px solid var(--border)', color: 'var(--muted)', letterSpacing: '0.06em' }}>
                  #{order.orderId}
                </span>
                <StatusBadge status={order.status} />
                {order.payment?.method === 'cod' && (
                  <span style={{ fontSize: 9, fontWeight: 700, color: '#0d47a1', background: '#e3f2fd', border: '1px solid #90caf9', padding: '2px 6px', letterSpacing: '0.08em' }}>COD</span>
                )}
              </div>
              <div style={{ fontSize: 11, color: 'var(--muted)', letterSpacing: '0.04em' }}>
                {order.createdAt ? format(new Date(order.createdAt), 'dd MMM yyyy') : ''}
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 24, color: 'var(--ink)', fontWeight: 300 }}>
                ₹{order.grandTotal?.toLocaleString('en-IN')}
              </div>
              <div style={{ fontSize: 10, color: 'var(--muted)', marginTop: 2 }}>incl. ₹{order.totalDeposit?.toLocaleString('en-IN')} deposit</div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--cream-dark)' }}>
            {order.items?.slice(0,3).map((item, i) => (
              <span key={i} style={{ fontSize: 10, background: 'var(--cream)', border: '1px solid var(--border)', padding: '3px 10px', color: 'var(--ink)', letterSpacing: '0.04em' }}>
                {item.productSnapshot?.name || 'Item'} · {item.size}
              </span>
            ))}
            {order.items?.length > 3 && <span style={{ fontSize: 11, color: 'var(--muted)' }}>+{order.items.length - 3} more</span>}
          </div>
          <Link to="/orders" style={{ display: 'inline-block', marginTop: 12, fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--gold)', textDecoration: 'none', borderBottom: '1px solid var(--gold)', paddingBottom: 1 }}>
            View Details
          </Link>
        </div>
      ))}
    </div>
  );
}

// ── Helpers ───────────────────────────────────────────────────────────────────
function Row({ label, children, last }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0', borderBottom: last ? 'none' : '1px solid var(--cream-dark)', gap: 12, flexWrap: 'wrap' }}>
      <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.18em', textTransform: 'uppercase', color: 'var(--muted)', minWidth: 90, flexShrink: 0 }}>{label}</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, justifyContent: 'flex-end', flexWrap: 'wrap' }}>{children}</div>
    </div>
  );
}

function Pill({ ok, yes, no }) {
  return (
    <span style={{ fontSize: 8, fontWeight: 700, letterSpacing: '0.12em', padding: '2px 7px', border: `1px solid ${ok ? 'rgba(45,106,45,0.4)' : 'rgba(139,32,32,0.3)'}`, color: ok ? '#2d6a2d' : '#8b2020', background: ok ? '#f0f7f0' : '#fdf0f0' }}>
      {ok ? yes : no}
    </span>
  );
}

function Chip({ children }) {
  return (
    <span style={{ fontSize: 8, fontWeight: 700, letterSpacing: '0.12em', padding: '2px 7px', border: '1px solid var(--border)', color: 'var(--muted)', background: 'var(--cream-dark)' }}>
      {children}
    </span>
  );
}

const C = {
  card:     { background: '#fff', border: '1px solid var(--border)', padding: 'clamp(16px,2.5vw,24px)' },
  head:     { fontFamily: "'Cormorant Garamond',serif", fontSize: 20, fontWeight: 400, color: 'var(--ink)' },
  val:      { fontSize: 13, color: 'var(--ink)' },
  ghostBtn: { fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', background: 'none', border: '1px solid var(--border-strong)', cursor: 'pointer', padding: '6px 14px', color: 'var(--muted)', transition: 'all 0.15s' },
  solidBtn: { fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', background: 'var(--ink)', color: 'var(--cream)', border: 'none', cursor: 'pointer', padding: '8px 18px', transition: 'opacity 0.15s' },
};